// UpgradePrompt — modal shown when free-tier users hit a paywall (e.g.
// watchlist limit, full-bid-history Pro lock).
//
// Sub-PR for #121 (three-tier pricing): the modal used to trigger a
// single-tier Stripe checkout directly. Now it routes the user to the
// public /pricing page so they see the Scout / Pro / Team comparison
// and pick a tier. This is a strict UX improvement — single-tier
// "Subscribe via Stripe" gave them no choice.

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { capture } from "../lib/analytics";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { UpgradeReasonCode, targetTierForUpgrade } from "../lib/upgradeReasons";

interface UpgradePromptProps {
  onClose: () => void;
  reason?: string;
  /** Which paywall opened the modal (lib/upgradeReasons). Drives analytics
   *  and, from PR-B on, which tier the modal leads with. */
  reasonCode?: UpgradeReasonCode;
  onRequireAuth?: () => void;
  /**
   * The viewer's plan. A free viewer is also told about Scout, the cheaper
   * tier; a Scout viewer is already on it, so only Pro is offered.
   */
  plan?: string;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ onClose, reason, reasonCode = "unknown", plan }) => {
  const navigate = useNavigate();
  const targetPlan = targetTierForUpgrade(plan, reasonCode);

  // Funnel "purchase intent" stage. The modal only mounts when a free or
  // Scout user hits a paywall, so mounting is the prompt being SHOWN; the
  // button below is the click. `upgrade_clicked` is the pre-2026-10-19 name
  // for the same mount event and is kept for 60 days so the saved insights
  // that read it do not go blank (it was never a click: @deprecated).
  useEffect(() => {
    capture("upgrade_prompt_shown", { reason_code: reasonCode, reason_copy: reason || "", plan: plan || "unknown", target_plan: targetPlan });
    capture("upgrade_clicked", { source_component: reason || "unknown", reason_code: reasonCode });
    // Fire once per open; reason is captured at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link the tier the gate actually needs. Pricing.tsx reads ?plan=
  // and ?interval= to preselect the card; ?reason= rides into pricing_viewed.
  const handleSeePricing = (cta: "primary" | "compare") => {
    capture("upgrade_cta_clicked", { reason_code: reasonCode, cta, target_plan: targetPlan, plan: plan || "unknown" });
    onClose();
    navigate(`/pricing?plan=${targetPlan}&interval=monthly&reason=${encodeURIComponent(reasonCode)}`);
  };

  const tierLabel = targetPlan === "scout" ? "Scout" : "Pro";
  const tierPrice = targetPlan === "scout" ? "$45" : "$75";
  // What the viewer gets on the tier being offered. Scout's list is true of
  // Scout; the old single list sold "unlimited watchlist" to everyone,
  // which Scout does not have (10).
  const benefits =
    targetPlan === "scout"
      ? [
          "Every property unlocked in 3 counties you choose",
          "Owner names, mailing addresses and equity on Seller Leads",
          "Bid Intelligence, Deal Analyzer and the full bid-history timeline",
          "10 watched properties, 15 saved searches, unlimited alerts",
        ]
      : [
          "Every property unlocked in every NC county",
          "Seller Leads statewide with CSV export",
          "Bid Intelligence, Deal Analyzer and the full bid-history timeline",
          "Unlimited watchlist saves and saved searches",
        ];

  return (
    <div className="auth-backdrop" onClick={onClose}>
      <div
        className="upgrade-modal"
        onClick={(e) => e.stopPropagation()}
        ref={useFocusTrap<HTMLDivElement>({ onEscape: onClose })}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-prompt-title"
      >
        <button className="auth-close" onClick={onClose} aria-label="Close">×</button>
        <div className="upgrade-icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6">
            <rect x="4.5" y="10" width="13" height="8.5" rx="1.6" />
            <path d="M7.2 10V7.3a3.8 3.8 0 017.6 0V10" />
          </svg>
        </div>
        <h2 className="upgrade-title" id="upgrade-prompt-title">Unlock {tierLabel}</h2>
        {reason && <p className="upgrade-reason">{reason}</p>}
        <div className="upgrade-benefits">
          {benefits.map((b) => (
            <div className="upgrade-benefit" key={b}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M3 8.5l3.2 3.2L13 4" />
              </svg>
              <span>{b}</span>
            </div>
          ))}
        </div>
        {plan !== "scout" && targetPlan === "pro" && (
          <p className="upgrade-reason">
            Or Scout at <span className="mono">$45</span>/mo: 3 counties you choose, a 10-property watchlist and 15 saved searches.
          </p>
        )}
        {plan !== "scout" && targetPlan === "scout" && (
          <p className="upgrade-reason">
            Need every county? Pro is <span className="mono">$75</span>/mo with unlimited saves and statewide leads.
          </p>
        )}
        <p className="upgrade-terms">
          14-day free trial for first-time subscribers. A card is required at checkout; cancel during the trial and you are not charged.
        </p>
        <div className="upgrade-actions">
          {/* Both route to /pricing (the #121 decision: show the comparison,
              never direct-checkout from here); the primary preselects the
              tier this gate needs. */}
          <button className="upgrade-btn-secondary" onClick={() => handleSeePricing("compare")}>
            Compare plans
          </button>
          <button className="upgrade-btn-primary" onClick={() => handleSeePricing("primary")}>
            Start 14-day {tierLabel} trial — <span className="mono">{tierPrice}</span>/mo
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
